import { motion } from "framer-motion";
import { memo, useCallback, useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { TConfirmTopMedScheduleForm } from "../confirmTopMedSchedueSchema";
import {
  Button,
  DateCalendarForm,
  FormInput,
  SchedulesList,
  useFetch,
  useFormContext,
} from "@4uhub/lib4uhub";
import { Box, Grid, InputAdornment, Typography, useTheme } from "@mui/material";
import { useTopMedSchedule } from "../../../../../store/contexts/TopMedScheduleContext/TopMedScheduleContext";
import NoData from "../../../../../components/UI/NoData";
import { saveTopMedSchedule } from "../../../../../services/topmedSchedule.service";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useExtensionConfigs } from "../../../../../hooks/useExtensionConfigs";
import { Extension } from "../../../../../models/intalled-extensions";

const TRANSLATION_PATH = "page.topmedSchedule.newSchedule.";

export const SecondStep = memo(() => {
  const { control, setValue, handleSubmit } =
    useFormContext<TConfirmTopMedScheduleForm>();

  const { t } = useTranslation();

  const { beneficiaryId } = useParams();

  const currentSpecialty = useWatch({ name: "specialty", control });

  const currentProfessional = useWatch({ name: "professional", control });

  const symptomsValue = useWatch({ name: "symptoms", control });

  const currentDate = useWatch({ name: "date", control });

  const { physicians, loading, onRegistered, scheduleDates } =
    useTopMedSchedule();

  const [findFullMonthParamBoolean, setFindFullMonthParamBoolean] =
    useState<boolean>(false);

  const { fetchExtensionConfig } = useExtensionConfigs();
  const fetchExtensions = useCallback(async () => {
    const findFullMonthParam = await fetchExtensionConfig(
      Extension.TOPMED_SCHEDULE,
      "FindFullMonth"
    );
    if (findFullMonthParam && findFullMonthParam.value === "true") {
      setFindFullMonthParamBoolean(true);
    }
  }, [fetchExtensionConfig]);
  const { sendRequest, loading: sLoading } = useFetch(saveTopMedSchedule);

  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  const submitHandler = useCallback(
    async (form: TConfirmTopMedScheduleForm) => {
      if (!beneficiaryId) return;

      const { success } = await sendRequest({
        beneficiaryId,
        hash: form.schedule ? form.schedule.hash : "",
        symptoms: form.symptoms ? form.symptoms : undefined,
      });

      if (success) {
        onRegistered();
      }
    },
    [beneficiaryId, sendRequest, onRegistered]
  );

  useEffect(() => {
    fetchExtensions();
  }, [fetchExtensions]);

  useEffect(() => {
    setValue("schedule", null);
  }, [currentDate, setValue]);

  if (!currentSpecialty) return null;

  return (
    <motion.div
      key={currentSpecialty.code}
      id={String(currentSpecialty.code)}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5, ease: [0.6, -0.05, 0.01, 0.99] }}
      style={{
        paddingTop: 20,
        width: "100%",
        height: "100%",
      }}
    >
      <Grid
        container
        item
        xs={12}
        sm={12}
        lg={8}
        width={"100%"}
        height={"fit-content"}
        spacing={1}
      >
        <Grid item xs={12} sm={6} lg={4}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <DateCalendarForm
              name="date"
              loading={loading}
              scheduleDates={scheduleDates}
              scheduledDayColor={primaryColor}
            />
            {findFullMonthParamBoolean && (
              <Box display="flex" flexWrap="wrap" gap={1.5} mt={-3.5}>
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      backgroundColor: primaryColor,
                      mr: 0.75,
                    }}
                  />
                  <Typography variant="body2">Data selecionada</Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      border: `2px solid ${primaryColor}`,
                      mr: 0.75,
                    }}
                  />
                  <Typography variant="body2">Dias disponíveis</Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          lg={8}
          sx={
            physicians.length === 0
              ? {
                  mt: 5,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }
              : undefined
          }
        >
          <motion.div
            key={physicians.length}
            id={String(physicians.length)}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, ease: [0.6, -0.05, 0.01, 0.99] }}
            style={{
              paddingTop: !loading && physicians.length === 0 ? 20 : 0,
              width: "100%",
              height: "fit-content",
              maxHeight: "50vh",
              overflowY: "auto",
            }}
          >
            {!loading && physicians.length === 0 && (
              <NoData message={t(TRANSLATION_PATH + "no_schedule")} />
            )}
            <SchedulesList
              schedules={physicians.filter((item) =>
                currentProfessional
                  ? item.name === currentProfessional.name
                  : true
              )}
              name="schedule"
              loading={loading}
            />
          </motion.div>
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <FormInput
            name="symptoms"
            label={t(TRANSLATION_PATH + "symptoms")}
            placeholder={`${t(TRANSLATION_PATH + "symptoms_placeholder")}`}
            fullWidth
            multiline
            minRows={3}
            inputProps={{ maxLength: 1000 }}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{
                    alignSelf: "flex-end",
                    paddingBottom: "6px",
                    paddingRight: "2px",
                  }}
                >
                  {`${symptomsValue?.length || 0}/1000`}
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sx={{ textAlign: "end" }}>
          <Button
            variant="contained"
            loading={sLoading}
            onClick={handleSubmit(submitHandler)}
          >
            {t(TRANSLATION_PATH + "schedule")}
          </Button>
        </Grid>
      </Grid>
    </motion.div>
  );
});
